import { useState, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { timeAgo } from '../../hooks/useTimeAgo'

interface FileRecord {
  id: string
  filename: string
  content_type: string
  size_bytes: number
  storage_key: string
  uploaded_by: string
  created_at: string
  url?: string
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function fileIcon(contentType: string): string {
  if (contentType.startsWith('audio/')) return '♪'
  if (contentType.startsWith('image/')) return '🖼'
  if (contentType.startsWith('video/')) return '▶'
  if (contentType.includes('pdf')) return '📄'
  return '📎'
}

export default function CommunityFilesPage() {
  const { user } = useAuth()
  const communityId = user?.communityId || `portal-${user?.communitySlug || ''}`
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
  const queryClient = useQueryClient()

  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement>(null)

  // Fetch files
  const { data: files, isLoading } = useQuery({
    queryKey: ['communityFiles', communityId],
    queryFn: async () => {
      const res = await fetch('/api/files', { headers })
      if (!res.ok) return []
      const data = await res.json()
      const arr = Array.isArray(data) ? data : (data.files || [])
      return arr.map((f: FileRecord) => ({
        ...f,
        url: f.storage_key ? `/uploads/${f.storage_key}` : undefined,
      })) as FileRecord[]
    },
    enabled: !!communityId,
  })

  // Upload
  const uploadFiles = useCallback(async (fileList: FileList | File[]) => {
    setUploading(true)
    const formData = new FormData()
    const filesArr = Array.from(fileList)
    setUploadProgress(`Uploading ${filesArr.length} file${filesArr.length > 1 ? 's' : ''}...`)

    for (const file of filesArr) {
      formData.append('files', file, file.name)
    }

    try {
      const res = await fetch('/api/files', {
        method: 'POST',
        headers: (token ? { Authorization: `Bearer ${token}` } : {}) as Record<string, string>,
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' }))
        setUploadProgress(`Error: ${err.error}`)
      } else {
        setUploadProgress(`Uploaded ${filesArr.length} file${filesArr.length > 1 ? 's' : ''}`)
        queryClient.invalidateQueries({ queryKey: ['communityFiles'] })
      }
    } catch {
      setUploadProgress('Upload failed — network error')
    }
    setTimeout(() => { setUploading(false); setUploadProgress('') }, 2000)
  }, [token, queryClient])

  // Delete
  const deleteMut = useMutation({
    mutationFn: async (fileId: string) => {
      const res = await fetch(`/api/files/${fileId}`, { method: 'DELETE', headers })
      if (!res.ok) throw new Error('Delete failed')
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['communityFiles'] }),
  })

  // Drag and drop
  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setDragging(true)
  }
  function handleDragLeave() { setDragging(false) }
  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files)
  }

  // Audio playback
  function togglePlay(file: FileRecord) {
    if (playingId === file.id) {
      audioRef.current?.pause()
      setPlayingId(null)
    } else {
      if (audioRef.current) {
        audioRef.current.src = file.url || ''
        audioRef.current.play()
        setPlayingId(file.id)
      }
    }
  }

  const fileList = files || []
  const audioFiles = fileList.filter(f => f.content_type?.startsWith('audio/'))
  const otherFiles = fileList.filter(f => !f.content_type?.startsWith('audio/'))

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <h1 className="text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Files</h1>

      {/* Upload zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className="rounded-xl p-8 mb-6 text-center cursor-pointer transition-colors"
        style={{
          background: dragging ? 'rgba(212,160,84,0.1)' : 'var(--bg-surface)',
          border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
        }}
      >
        {uploading ? (
          <div className="text-sm" style={{ color: 'var(--accent)' }}>{uploadProgress}</div>
        ) : (
          <>
            <div className="text-2xl mb-2">📁</div>
            <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              Drop files here or click to upload
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              Audio, images, documents — any file type
            </div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => { if (e.target.files?.length) uploadFiles(e.target.files) }}
        />
      </div>

      {/* Hidden audio element for playback */}
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} />

      {isLoading ? (
        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading files...</div>
      ) : fileList.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No files uploaded yet</p>
        </div>
      ) : (
        <>
          {/* Audio files */}
          {audioFiles.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>
                Audio ({audioFiles.length})
              </h2>
              <div className="flex flex-col gap-1.5">
                {audioFiles.map(f => (
                  <div
                    key={f.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg"
                    style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
                  >
                    <button
                      onClick={() => togglePlay(f)}
                      className="text-lg shrink-0"
                      style={{ background: 'none', border: 'none', color: playingId === f.id ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer', width: 28 }}
                    >
                      {playingId === f.id ? '⏸' : '▶'}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{f.filename}</div>
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {formatSize(f.size_bytes)} · {timeAgo(f.created_at)} · {f.uploaded_by}
                      </div>
                    </div>
                    <a
                      href={f.url}
                      download={f.filename}
                      className="text-xs px-2 py-1 rounded no-underline"
                      style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
                      onClick={e => e.stopPropagation()}
                    >
                      ↓
                    </a>
                    <button
                      onClick={() => { if (confirm(`Delete ${f.filename}?`)) deleteMut.mutate(f.id) }}
                      className="text-xs px-2 py-1 rounded"
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Other files */}
          {otherFiles.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)' }}>
                Files ({otherFiles.length})
              </h2>
              <div className="flex flex-col gap-1.5">
                {otherFiles.map(f => (
                  <div
                    key={f.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg"
                    style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
                  >
                    <span className="text-lg shrink-0">{fileIcon(f.content_type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{f.filename}</div>
                      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {formatSize(f.size_bytes)} · {timeAgo(f.created_at)} · {f.uploaded_by}
                      </div>
                    </div>
                    <a
                      href={f.url}
                      download={f.filename}
                      className="text-xs px-2 py-1 rounded no-underline"
                      style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
                    >
                      ↓
                    </a>
                    <button
                      onClick={() => { if (confirm(`Delete ${f.filename}?`)) deleteMut.mutate(f.id) }}
                      className="text-xs px-2 py-1 rounded"
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}
